<script setup lang="ts">
import type {
  EnrichmentRunSummary,
  EnrichmentStatusEntry,
  RefreshCheckedReason,
  RefreshOutcomeDto,
} from '@factoryfixinc/ats-interfaces';
import { computed, ref, watch } from 'vue';

import CheckIcon from '@/assets/svg/check-16.svg?component';
import EnrichmentTraceCloseButton from '@/components/Common/EnrichmentTraceCloseButton.vue';
import EnrichmentTracePanelHeader from '@/components/Common/EnrichmentTracePanelHeader.vue';
import EnrichmentTraceRowIcon from '@/components/Common/EnrichmentTraceRowIcon.vue';
import EnrichmentTraceSourceSummary from '@/components/Common/EnrichmentTraceSourceSummary.vue';
import {
  mapEnrichmentLoadingPanel,
  mapEnrichmentTracePanel,
} from '@/components/Common/trace-panel.mapper';
import DateUtils from '@/utils/date-utils';

interface Props {
  celebrateAt?: number;
  outcome: RefreshOutcomeDto | null;
  fieldStates?: EnrichmentStatusEntry['fields'];
  result?: EnrichmentRunSummary | null;
  isLoading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  celebrateAt: undefined,
  fieldStates: undefined,
  result: null,
  isLoading: false,
});

const emit = defineEmits<{
  (e: 'close'): void;
}>();

// The outcome can already be the terminal DTO while isLoading is true (it's set ahead
// of the finalizing beat), so gate the receipt on isLoading, not on the outcome value.
const loadingModel = computed(() =>
  props.isLoading ? mapEnrichmentLoadingPanel(props.fieldStates) : null,
);
const reason = computed<RefreshCheckedReason | undefined>(() =>
  props.outcome?.outcome === 'checked' ? props.outcome.reason : undefined,
);
const relativeTime = computed(() => {
  const outcome = props.outcome;
  return outcome && outcome.outcome !== 'checking' ? DateUtils.timeAgo(outcome.at) : undefined;
});
// Same precedence as EnrichmentChip's tooltip count, so the two never disagree.
const outcomeSourcesChecked = computed(() => {
  const outcome = props.outcome;
  return outcome && outcome.outcome !== 'checking' ? outcome.sourcesChecked : undefined;
});
const model = computed(() =>
  props.isLoading
    ? null
    : mapEnrichmentTracePanel(
        props.outcome?.outcome ?? null,
        reason.value,
        props.fieldStates,
        props.result,
        {
          relativeTime: relativeTime.value,
          outcomeSourcesChecked: outcomeSourcesChecked.value,
        },
      ),
);
const ariaLabel = computed(() => {
  if (loadingModel.value) return 'Freshness check in progress';
  return model.value?.variant === 'instant'
    ? model.value.footer.headline
    : 'Freshness check details';
});
const celebrationDelayMs = computed(() =>
  Math.max(0, (props.celebrateAt ?? Date.now()) - Date.now()),
);
const celebrationStyle = computed(() => ({
  '--enrichment-celebrate-delay': `${celebrationDelayMs.value}ms`,
}));
// The dialog's aria-label only reads once on focus, so an in-place loading -> terminal
// change needs a live region. Skipped when the panel opens already resolved, since the
// aria-label covers that and repeating it would double-announce.
const hasBeenLoading = ref(false);
watch(
  loadingModel,
  (value) => {
    if (value) hasBeenLoading.value = true;
  },
  { immediate: true },
);
const phaseAnnouncement = computed(() => {
  if (loadingModel.value) return 'Checking';
  return hasBeenLoading.value ? model.value?.footer.headline : undefined;
});

const SPARK_OFFSETS = [
  { '--spark-x': '21px', '--spark-y': '6px' },
  { '--spark-x': '6px', '--spark-y': '21px' },
  { '--spark-x': '-15px', '--spark-y': '15px' },
  { '--spark-x': '-21px', '--spark-y': '-6px' },
  { '--spark-x': '-6px', '--spark-y': '-21px' },
  { '--spark-x': '15px', '--spark-y': '-15px' },
];

function close(): void {
  emit('close');
}
</script>

<template>
  <aside
    v-if="loadingModel || model"
    role="dialog"
    aria-modal="false"
    :aria-label="ariaLabel"
    data-test-id="enrichment-trace-panel"
    :data-celebrate-at="celebrateAt"
    class="enrichment-trace-panel menu-scrollbar max-h-[min(640px,calc(100vh-24px))] overflow-y-auto rounded-xl border border-tint-80 bg-tint-0 px-4 pb-3 pt-3 text-left shadow-menu"
    :class="{
      'enrichment-trace-panel--instant flex w-[min(360px,calc(100vw-24px))] items-start gap-2.5 p-3.5':
        model?.variant === 'instant',
      'w-[min(500px,calc(100vw-24px))]': model?.variant !== 'instant',
      'enrichment-trace-panel--positive':
        model?.variant !== 'instant' && model?.footer.tone === 'positive',
      'enrichment-trace-panel--quiet':
        model?.variant !== 'instant' && model?.footer.tone === 'neutral',
    }"
    :style="celebrationStyle"
  >
    <span class="sr-only" role="status" aria-live="polite">{{ phaseAnnouncement }}</span>

    <template v-if="loadingModel">
      <EnrichmentTracePanelHeader :subline="loadingModel.subline" @close="close">
        <template #badge>
          <span
            data-test-id="enrichment-trace-checking-badge"
            aria-hidden="true"
            class="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-highlight-500/[0.08] px-2.5 py-1 text-2xs font-bold uppercase tracking-[0.5px] text-highlight-500"
          >
            <span
              class="h-[9px] w-[9px] shrink-0 animate-spin rounded-full border-2 border-highlight-500/25 border-t-highlight-500 motion-reduce:animate-none motion-reduce:border-t-highlight-500/25"
            />
            Checking
          </span>
        </template>
      </EnrichmentTracePanelHeader>

      <section aria-label="Enrichment check details" class="flex flex-col gap-[13px]">
        <div
          v-for="(row, index) in loadingModel.rows"
          :key="row.id"
          :data-test-id="`enrichment-trace-loading-row-${row.id}`"
          :data-state="row.state"
          class="enrichment-trace-panel__row flex items-stretch gap-2.5"
          :style="{ '--trace-row-index': index }"
        >
          <EnrichmentTraceRowIcon
            :icon="row.icon"
            :checking="row.state === 'checking'"
            :is-last="index === loadingModel.rows.length - 1"
          />

          <span class="min-w-0 flex-1">
            <span class="block text-[13.5px] font-650 leading-4.5 text-shade-880">
              {{ row.headline }}
            </span>
            <span
              class="block text-[12.5px] leading-4.5"
              :class="row.state === 'checking' ? 'text-tint-400' : 'text-shade-800'"
            >
              {{ row.state === 'checking' ? 'Checking...' : 'Reviewed' }}
            </span>
          </span>
        </div>
      </section>
    </template>

    <template v-else-if="model?.variant === 'instant'">
      <span
        class="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-primary-500 text-tint-0"
        aria-hidden="true"
      >
        <CheckIcon
          class="h-[13px] w-[13px]"
          viewBox="0 0 16 16"
          data-test-id="enrichment-trace-instant-check-icon"
        />
      </span>

      <span class="min-w-0 flex-1">
        <span class="block text-[13.5px] font-bold leading-4.5 text-shade-880">
          {{ model.footer.headline }}
        </span>
        <span class="mt-0.5 block text-xs leading-[17px] text-shade-800">
          {{ model.footer.subline }}
        </span>
      </span>

      <EnrichmentTraceCloseButton label="Close contact details confirmation" @close="close" />
    </template>

    <template v-else-if="model">
      <EnrichmentTracePanelHeader :subline="model.claim" @close="close" />

      <EnrichmentTraceSourceSummary
        v-if="model.sourceSummary"
        :summary="model.sourceSummary"
        :start-at="celebrateAt"
      />

      <section
        v-if="model.rows.length"
        aria-label="Enrichment check details"
        class="flex flex-col gap-[13px]"
      >
        <div
          v-for="(row, index) in model.rows"
          :key="row.id"
          :data-test-id="`enrichment-trace-row-${row.id}`"
          class="enrichment-trace-panel__row flex items-stretch gap-2.5"
          :style="{ '--trace-row-index': index }"
        >
          <EnrichmentTraceRowIcon
            :icon="row.icon"
            :is-last="index === model.rows.length - 1"
            animated
          />

          <span class="min-w-0 flex-1">
            <span class="block text-[13.5px] font-650 leading-4.5 text-shade-880">
              {{ row.headline }}
            </span>
            <span class="block text-[12.5px] leading-4.5 text-shade-800">
              {{ row.subline }}
            </span>
            <span v-if="row.details?.length" class="mt-1 flex flex-col gap-0.5">
              <span
                v-for="detail in row.details"
                :key="detail"
                class="flex items-start gap-1.5 text-xs leading-4 text-tint-400"
              >
                <span class="mt-1.5 h-[3px] w-[3px] shrink-0 rounded-full bg-tint-100" />
                <span>{{ detail }}</span>
              </span>
            </span>
          </span>
        </div>
      </section>

      <footer
        data-test-id="enrichment-trace-footer"
        class="enrichment-trace-panel__footer -mx-4 -mb-3 mt-3 flex items-center gap-2.5 rounded-b-xl border-t px-4 py-2.5"
        :class="{
          'enrichment-trace-panel__footer--positive border-enrichment-band-edge bg-enrichment-verdict':
            model.footer.tone === 'positive',
          'enrichment-trace-panel__footer--neutral border-tint-80 bg-tint-20':
            model.footer.tone === 'neutral',
        }"
      >
        <span class="relative flex shrink-0" aria-hidden="true">
          <span
            class="enrichment-trace-panel__footer-icon flex h-[26px] w-[26px] items-center justify-center rounded-full"
            :class="
              model.footer.tone === 'positive'
                ? 'bg-primary-500 text-tint-0'
                : 'bg-tint-80 text-shade-800'
            "
          >
            <CheckIcon
              v-if="model.footer.tone === 'positive'"
              class="h-[13px] w-[13px]"
              viewBox="0 0 16 16"
              data-test-id="enrichment-trace-footer-check-icon"
            />
            <span
              v-else
              class="bg-current h-0.5 w-3 rounded-full"
              data-test-id="enrichment-trace-footer-minus-icon"
            />
          </span>
          <span
            v-if="model.footer.tone === 'positive'"
            class="enrichment-trace-panel__sparks pointer-events-none absolute left-1/2 top-1/2 h-0 w-0"
          >
            <span
              v-for="(spark, sparkIndex) in SPARK_OFFSETS"
              :key="sparkIndex"
              class="absolute h-1 w-1 rounded-full opacity-0"
              :class="sparkIndex % 2 ? 'bg-highlight-500/85' : 'bg-primary-500'"
              :style="spark"
            />
          </span>
        </span>

        <span class="min-w-0">
          <span
            class="block text-sm font-bold leading-4.5 tracking-[0.1px]"
            :class="
              model.footer.tone === 'positive' ? 'text-enrichment-footer-text' : 'text-shade-800'
            "
          >
            {{ model.footer.headline }}
          </span>
          <span v-if="model.footer.subline" class="block text-xs leading-4 text-shade-800">
            {{ model.footer.subline }}
          </span>
        </span>
      </footer>
    </template>
  </aside>
</template>

<style scoped lang="postcss">
.enrichment-trace-panel__row {
  animation: enrichment-trace-row-in 360ms calc(140ms + var(--trace-row-index) * 90ms)
    cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

/* .enrichment-trace-panel__row-icon's own animation rule lives in
   EnrichmentTraceRowIcon.vue — a scoped rule here could never match a class
   applied inside that child component. enrichment-trace-icon-pop stays global
   (referenced by name from both files) since __footer-icon below still uses it. */

.enrichment-trace-panel__footer--positive {
  background-size: 260% 100%;
  animation:
    enrichment-trace-footer-in 500ms var(--enrichment-celebrate-delay)
      cubic-bezier(0.2, 0.8, 0.2, 1) both,
    enrichment-trace-footer-sweep 1s var(--enrichment-celebrate-delay) ease-out both;
}

.enrichment-trace-panel__footer--neutral {
  animation: enrichment-trace-footer-in 320ms var(--enrichment-celebrate-delay) ease-out both;
}

.enrichment-trace-panel--positive .enrichment-trace-panel__footer-icon {
  animation: enrichment-trace-icon-pop 450ms calc(var(--enrichment-celebrate-delay) + 120ms)
    cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

.enrichment-trace-panel__sparks > span {
  animation: enrichment-trace-spark 650ms calc(var(--enrichment-celebrate-delay) + 190ms) ease-out
    both;
}

@keyframes enrichment-trace-row-in {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes enrichment-trace-icon-pop {
  from {
    transform: scale(0.72);
  }
  70% {
    transform: scale(1.12);
  }
  to {
    transform: scale(1);
  }
}

@keyframes enrichment-trace-footer-in {
  from {
    opacity: 0;
    transform: translateY(7px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes enrichment-trace-footer-sweep {
  from {
    background-position: 100% 0;
  }
  to {
    background-position: 0 0;
  }
}

@keyframes enrichment-trace-spark {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.8);
  }
  25% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translate(var(--spark-x), var(--spark-y)) scale(0.45);
  }
}

@keyframes enrichment-trace-panel-fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .enrichment-trace-panel__row,
  .enrichment-trace-panel__footer,
  .enrichment-trace-panel__footer-icon,
  .enrichment-trace-panel__sparks > span {
    animation: enrichment-trace-panel-fade 120ms ease-out both;
  }
}
</style>
